import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend, Cell,
} from "recharts";
import {
  Package, Trash, Eye, Copy, FileXls, FilePdf, MagnifyingGlass,
  ChartLineUp, Stack, Scales, CurrencyInr, Calendar, Buildings,
  ArrowLeft, FunnelSimple, Plus, ChartPieSlice,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import Header from "../components/Header";
import Footer from "../components/Footer";
import MonthlySpendAnalysis from "../components/MonthlySpendAnalysis";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api, API, fmtINR, TOKEN_KEY } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const PRODUCTION_FOR = ["All", "Restaurant", "Cloud Kitchen", "Bulk Order", "Catering", "Event", "Other"];

const COLORS = ["#0A3B24", "#D4AF37", "#7A9E5C", "#16A34A", "#0E5232", "#A66B00"];

export default function ProductionHistoryPage() {
  const navigate = useNavigate();
  const { isAdmin, guard } = useAuth();
  const [plans, setPlans] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [forFilter, setForFilter] = useState("All");

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get("/production/v2/plans"),
        api.get("/production/v2/analytics"),
      ]);
      setPlans(p.data || []);
      setAnalytics(a.data || null);
    } catch {
      toast.error("Failed to load production history");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    return plans.filter((p) => {
      if (forFilter !== "All" && (p.production_for || "") !== forFilter) return false;
      if (fromDate && (p.production_date || "") < fromDate) return false;
      if (toDate && (p.production_date || "") > toDate) return false;
      if (q) {
        const hay = `${p.name || ""} ${p.production_for || ""} ${p.notes || ""} ${(p.items || []).map((i) => i.recipe_name).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [plans, search, fromDate, toDate, forFilter]);

  const handleDelete = guard(async (plan) => {
    if (!window.confirm(`Delete "${plan.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/production/v2/plans/${plan.id}`);
      toast.success("Plan deleted");
      load();
    } catch { toast.error("Delete failed"); }
  }, "delete the plan");

  const handleDuplicate = guard(async (plan) => {
    try {
      const { data } = await api.post(`/production/v2/plans/${plan.id}/duplicate`);
      toast.success(`Duplicated as "${data.name}"`);
      load();
    } catch { toast.error("Duplicate failed"); }
  }, "duplicate the plan");

  const handleDownload = async (planId, kind) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/production/v2/plans/${planId}/export/${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const plan = plans.find((p) => p.id === planId);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `KOSTRA_Production_Plan_${(plan?.name || "plan").replace(/[^a-z0-9]+/gi, "_")}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch { toast.error("Download failed"); }
  };

  const dlExcel = guard((id) => handleDownload(id, "excel"), "download Excel");
  const dlPdf = guard((id) => handleDownload(id, "pdf"), "download PDF");

  const kpis = analytics?.kpis || {};
  const byDate = (analytics?.by_date || []).slice(-30); // last 30 dates
  const byFor = analytics?.by_production_for || [];
  const topRecipes = analytics?.top_recipes || [];

  return (
    <div className="app-shell">
      <Header />

      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-medium inline-flex items-center gap-2">
                <ChartLineUp size={14} weight="fill" /> History &amp; Analytics
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
                Every production day, <br className="hidden md:block" />
                <span className="text-[#D4AF37]">measured.</span>
              </h1>
              <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl">
                Review every saved production plan, download any of them, duplicate the winners, and watch your
                kitchen trend over time.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/production">
                <Button
                  variant="outline"
                  className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium h-11"
                  data-testid="back-to-builder-btn"
                >
                  <ArrowLeft size={16} weight="bold" className="mr-2" /> Back to Builder
                </Button>
              </Link>
              <Link to="/production">
                <Button
                  className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11"
                  data-testid="new-plan-btn"
                >
                  <Plus size={16} weight="bold" className="mr-2" /> New Plan
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 space-y-8">
        {!isAdmin && (
          <div className="kostra-card p-6 text-center">
            <p className="text-sm text-[#4B5563]">Admin login required to view production history.</p>
          </div>
        )}

        {isAdmin && (
          <Tabs defaultValue="history" className="space-y-6" data-testid="history-tabs">
            <TabsList className="bg-white border border-[#E5E7EB] p-1 h-auto rounded-lg shadow-sm">
              <TabsTrigger
                value="history"
                className="data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white px-4 py-2 text-sm font-semibold rounded-md"
                data-testid="tab-history"
              >
                <ChartLineUp size={14} weight="bold" className="mr-2" /> Plans &amp; Analytics
              </TabsTrigger>
              <TabsTrigger
                value="monthly"
                className="data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white px-4 py-2 text-sm font-semibold rounded-md"
                data-testid="tab-monthly-spend"
              >
                <ChartPieSlice size={14} weight="bold" className="mr-2" /> Monthly Spend Analysis
              </TabsTrigger>
            </TabsList>

            <TabsContent value="history" className="space-y-8 mt-0">
              <>
            {/* KPI Strip */}
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="history-kpis">
              <KpiCard label="Plans" value={kpis.plan_count || 0} icon={Package} accent="#0A3B24" />
              <KpiCard label="Total Cost" value={fmtINR(kpis.total_cost || 0)} icon={CurrencyInr} accent="#0E5232" />
              <KpiCard label="Total Weight" value={`${(kpis.total_weight_kg || 0).toFixed(2)} kg`} icon={Scales} accent="#D4AF37" />
              <KpiCard label="Total Portions" value={kpis.total_portions || 0} icon={Stack} accent="#16A34A" />
              <KpiCard label="Avg Cost / Kg" value={fmtINR(kpis.avg_cost_per_kg || 0)} icon={CurrencyInr} accent="#A66B00" />
              <KpiCard label="Top Recipe" value={kpis.top_recipe || "—"} icon={ChartLineUp} accent="#7A9E5C" small />
            </section>

            {/* Charts */}
            {byDate.length > 0 && (
              <section className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="history-charts">
                <div className="kostra-card p-5 md:p-6 lg:col-span-2" data-testid="trend-chart">
                  <h3 className="kostra-section-title text-lg flex items-center gap-2">
                    <ChartLineUp size={18} weight="duotone" /> Production Trend
                  </h3>
                  <p className="text-xs text-[#4B5563] mt-0.5">Last {byDate.length} production date{byDate.length > 1 ? "s" : ""}</p>
                  <div className="mt-4 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={byDate} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(value, name) =>
                            name === "cost" ? [fmtINR(value), "Cost"] :
                            name === "weight_kg" ? [`${value} kg`, "Weight"] :
                            [value, name]
                          }
                          contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 6 }}
                        />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#0A3B24" strokeWidth={2} name="Cost (₹)" dot={{ r: 3 }} />
                        <Line yAxisId="right" type="monotone" dataKey="weight_kg" stroke="#D4AF37" strokeWidth={2} name="Weight (kg)" dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="kostra-card p-5 md:p-6" data-testid="by-for-chart">
                  <h3 className="kostra-section-title text-lg flex items-center gap-2">
                    <Buildings size={18} weight="duotone" /> Cost by Production For
                  </h3>
                  <div className="mt-4 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={byFor} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="production_for" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 6 }} />
                        <Bar dataKey="cost" fill="#0A3B24" radius={[4, 4, 0, 0]}>
                          {byFor.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </section>
            )}

            {/* Top Recipes */}
            {topRecipes.length > 0 && (
              <section className="kostra-card p-5 md:p-6" data-testid="top-recipes">
                <h3 className="kostra-section-title text-lg flex items-center gap-2">
                  <ChartLineUp size={18} weight="duotone" /> Top Recipes by Cost Contribution
                </h3>
                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                        <th className="py-2 pr-2">#</th>
                        <th className="py-2 pr-2">Recipe</th>
                        <th className="py-2 pr-2 text-center">Appearances</th>
                        <th className="py-2 pr-2 text-right">Total Portions</th>
                        <th className="py-2 pr-2 text-right">Total Weight</th>
                        <th className="py-2 pl-2 text-right">Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRecipes.map((r, i) => (
                        <tr key={r.recipe_id} className="border-b border-[#F1F2F4]">
                          <td className="py-2 pr-2 font-mono-num text-[#4B5563]">{i + 1}</td>
                          <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{r.recipe_name}</td>
                          <td className="py-2 pr-2 text-center font-mono-num">{r.appearances}</td>
                          <td className="py-2 pr-2 text-right font-mono-num">{r.total_portions}</td>
                          <td className="py-2 pr-2 text-right font-mono-num">{(r.total_weight_kg || 0).toFixed(2)} kg</td>
                          <td className="py-2 pl-2 text-right font-mono-num font-semibold">{fmtINR(r.total_cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Filters + List */}
            <section className="kostra-card p-5 md:p-6" data-testid="history-list">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <h3 className="kostra-section-title text-lg flex items-center gap-2">
                  <FunnelSimple size={18} weight="duotone" /> All Plans
                </h3>
                <span className="gold-pill text-[11px] font-semibold px-2.5 py-1 rounded-full">
                  {filtered.length} of {plans.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4">
                <div className="md:col-span-4">
                  <Label className="kostra-data-label">Search</Label>
                  <div className="relative mt-1">
                    <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
                    <Input
                      value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="Plan name, recipe, notes..."
                      className="pl-9 h-10"
                      data-testid="history-search"
                    />
                  </div>
                </div>
                <div className="md:col-span-3">
                  <Label className="kostra-data-label">Production For</Label>
                  <Select value={forFilter} onValueChange={setForFilter}>
                    <SelectTrigger className="mt-1 h-10" data-testid="history-for-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCTION_FOR.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="kostra-data-label">From Date</Label>
                  <Input
                    type="date" value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="mt-1 h-10 font-mono-num"
                    data-testid="history-from-date"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="kostra-data-label">To Date</Label>
                  <Input
                    type="date" value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="mt-1 h-10 font-mono-num"
                    data-testid="history-to-date"
                  />
                </div>
                <div className="md:col-span-1 flex items-end">
                  <Button
                    variant="outline"
                    onClick={() => { setSearch(""); setFromDate(""); setToDate(""); setForFilter("All"); }}
                    className="h-10 w-full text-xs"
                    data-testid="history-reset-filters"
                  >
                    Reset
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="py-10 text-center text-sm text-[#4B5563]">Loading...</div>
              ) : filtered.length === 0 ? (
                <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-10 text-center">
                  <Package size={36} weight="duotone" className="mx-auto text-[#0A3B24]" />
                  <div className="text-sm text-[#111827] font-semibold mt-2">No plans match these filters</div>
                  {plans.length === 0 && (
                    <Link to="/production">
                      <Button className="mt-3 bg-[#0A3B24] hover:bg-[#0E5232] text-white" data-testid="create-first-plan-btn">
                        <Plus size={14} weight="bold" className="mr-2" /> Create your first plan
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filtered.map((p) => (
                    <div key={p.id}
                         className="rounded-xl border border-[#E5E7EB] bg-white hover:border-[#D4AF37] hover:shadow-sm transition p-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-center"
                         data-testid={`history-row-${p.id}`}>
                      <div className="lg:col-span-4">
                        <button
                          type="button"
                          onClick={() => navigate(`/production?plan=${p.id}`)}
                          className="text-left group"
                          data-testid={`history-open-${p.id}`}
                        >
                          <div className="font-semibold text-[#0A3B24] text-base group-hover:underline decoration-[#D4AF37] underline-offset-4">
                            {p.name}
                          </div>
                          <div className="text-[11px] text-[#4B5563] mt-0.5 flex items-center gap-1.5">
                            <Calendar size={11} weight="bold" /> {p.production_date || "—"}
                            <span className="text-[#D4AF37]">·</span>
                            <Buildings size={11} weight="bold" /> {p.production_for || "—"}
                          </div>
                        </button>
                      </div>
                      <div className="lg:col-span-5 grid grid-cols-4 gap-2">
                        <Mini label="Items" value={p.items?.length || 0} />
                        <Mini label="Portions" value={p.totals?.total_portions || 0} />
                        <Mini label="Weight" value={`${(p.totals?.total_weight_kg || 0).toFixed(2)} kg`} />
                        <Mini label="Cost" value={fmtINR(p.totals?.total_cost || 0)} accent="#0A3B24" />
                      </div>
                      <div className="lg:col-span-3 flex items-center justify-end gap-1.5 flex-wrap">
                        <button
                          type="button"
                          onClick={() => navigate(`/production/preview/${p.id}`)}
                          className="h-8 px-2.5 rounded-md text-xs font-semibold bg-[#FDF9EE] text-[#0A3B24] hover:bg-[#F4E9C5] border border-[#D4AF37]/30 inline-flex items-center gap-1"
                          data-testid={`history-preview-${p.id}`}
                        >
                          <Eye size={12} weight="bold" /> Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => dlExcel(p.id)}
                          className="h-8 px-2.5 rounded-md text-xs font-semibold bg-[#D4AF37] text-[#0A3B24] hover:bg-[#C5A028] inline-flex items-center gap-1"
                          data-testid={`history-excel-${p.id}`}
                        >
                          <FileXls size={12} weight="bold" /> Excel
                        </button>
                        <button
                          type="button"
                          onClick={() => dlPdf(p.id)}
                          className="h-8 px-2.5 rounded-md text-xs font-semibold bg-[#0A3B24] text-white hover:bg-[#0E5232] inline-flex items-center gap-1"
                          data-testid={`history-pdf-${p.id}`}
                        >
                          <FilePdf size={12} weight="bold" /> PDF
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDuplicate(p)}
                          className="h-8 w-8 rounded-md text-[#4B5563] hover:bg-[#FDF9EE] hover:text-[#0A3B24] border border-[#E5E7EB] inline-flex items-center justify-center"
                          title="Duplicate"
                          data-testid={`history-duplicate-${p.id}`}
                        >
                          <Copy size={13} weight="bold" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(p)}
                          className="h-8 w-8 rounded-md text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] border border-[#E5E7EB] inline-flex items-center justify-center"
                          title="Delete"
                          data-testid={`history-delete-${p.id}`}
                        >
                          <Trash size={13} weight="bold" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
            </TabsContent>

            <TabsContent value="monthly" className="mt-0">
              <MonthlySpendAnalysis />
            </TabsContent>
          </Tabs>
        )}
      </main>
      <Footer />
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, accent, small }) {
  return (
    <div className="kostra-card p-4">
      <div className="flex items-start justify-between">
        <span className="kostra-data-label text-[10px]">{label}</span>
        <div className="h-8 w-8 rounded-md flex items-center justify-center" style={{ background: `${accent}10`, color: accent }}>
          <Icon size={16} weight="duotone" />
        </div>
      </div>
      <div className={`mt-2 font-mono-num font-bold text-[#111827] ${small ? "text-sm" : "text-xl"}`}>{value}</div>
    </div>
  );
}

function Mini({ label, value, accent = "#111827" }) {
  return (
    <div>
      <div className="kostra-data-label text-[10px]">{label}</div>
      <div className="font-mono-num text-xs font-semibold mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}
